import React, { useState, useMemo } from 'react';
import { ListChecks, Check, X, Trash2, FileText, Eye, Download, LayoutGrid, Paperclip, Pencil, ChevronRight, UserPlus, Users, Share2, Calendar, DollarSign, Send, Tag, Contact as ContactIcon, StickyNote, Bell, MessageSquare, Undo2, LogOut, ScrollText, Repeat } from 'lucide-react';
import { fmtAmt, runPerItem, emit, rowBtn, removeBtnClass } from './cardHelpers';
import { persistResolution } from './ResolutionCard';
import { CARD_ACTION_ROW, CARD_BTN_LABEL, CARD_ICON } from './cardLayout';
import { tNodes, useLanguage } from '../../i18n';
import { seriesSummary } from '../../utils/calendarSeries';
import { documentService } from '../../services/documentService';
import { expenseService } from '../../services/expenseService';
import { calendarService } from '../../services/calendarService';
import { boardService } from '../../services/boardService';
import { contactService } from '../../services/contactService';
import { teamService } from '../../services/teamService';
import { noteService } from '../../services/noteService';
import { taskService } from '../../services/taskService';
import { reminderService } from '../../services/reminderService';
import { actionService } from '../../services/actionService';
import { workspaceService } from '../../services/workspaceService';
import apiService from '../../services/apiService';
import { userErrorMessage } from '../../utils/userError';
import AttachedFileChoice, { hasAttachedFile, LinkedFilesChoice } from '../finance/AttachedFileChoice';

// ---------------------------------------------------------------------------
// Per-card configs consumed by <ResolutionCard>. Each config captures EXACTLY
// what varies between the ~14 previously-copy-pasted cards; the engine owns the
// shared status/busy/error/removedIds/persist machine. (H-3.2)
//
// Pixel/behaviour parity is a hard requirement: testids, labels, button order,
// per-row X semantics, dynamic count labels, Preview/Download, and deep links
// are all preserved so the existing E2E specs pass unchanged.
// ---------------------------------------------------------------------------

const plural = (n) => (n === 1 ? '' : 's');

// A per-row X button (drop from queue). `show` gates visibility.
const RemoveX = ({ show, onClick, disabled, title, aria }) => (
    show ? (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={removeBtnClass}
            title={title}
            aria-label={aria}
        >
            <X size={14} />
        </button>
    ) : null
);

// =========================================================================
// 1. Document deletion (document_delete_request) — Yes/No buttons, Preview +
// Download per row, X only when >1 remains. testid: delete-confirm-card
// =========================================================================
export const documentDeleteConfig = {
    cardKey: 'document_delete_request',
    testId: 'delete-confirm-card',
    summaryHasTestId: false,
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => {
        if (Array.isArray(p?.documents) && p.documents.length) return p.documents.some((d) => d?.document_id);
        return !!p?.document_id;
    },
    getItems: (p) => {
        if (Array.isArray(p?.documents) && p.documents.length) return p.documents.filter((d) => d?.document_id);
        if (p?.document_id) return [{ document_id: p.document_id, filename: p.filename, mime_type: p.mime_type, is_image: p.is_image }];
        return [];
    },
    itemKey: (d) => d.document_id,
    title: ({ t, count }) => t('docDeleteConfirmTitle', { n: count, s: plural(count) }),
    renderRow: ({ item: d, ctx }) => {
        const { busy, active, removeFromQueue, onPreview, onDownload, t } = ctx;
        return (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
                <FileText size={16} className="text-rose-500 flex-shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm text-white" title={d.filename}>{d.filename || t('cardDocumentFallback', 'document')}</span>
                <button type="button" onClick={() => onPreview(d.document_id, d.filename)} className={rowBtn('rose')} title={t('commonPreview', 'Preview')}><Eye size={12} /> {t('commonPreview', 'Preview')}</button>
                <button type="button" onClick={() => onDownload(d.document_id, d.filename)} className={rowBtn('rose')} title={t('commonDownload', 'Download')}><Download size={12} /> {t('commonDownload', 'Download')}</button>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(d.document_id)}
                    disabled={busy}
                    title={t('cardKeepItem', 'Keep {name} — remove from deletion', { name: d.filename || t('cardThisFile', 'this file') })}
                    aria={`Remove ${d.filename || 'this file'} from deletion`}
                />
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Check,
        label: ({ t }) => t('cardYes'),
        busyLabel: ({ t }) => t('docDeleteBusy'),
        run: async ({ items }) => runPerItem(items, async (d) => {
            await documentService.deleteDocument(d.document_id);
            emit('omnis:document-deleted', { id: d.document_id, filename: d.filename });
        }, { label: (d) => d.filename || d.document_id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    cancelLabel: ({ t }) => t('cardNo'),
    footerNote: ({ t }) => t('docDeleteCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('docDeleteDone', { n, s: plural(n) })}</>;
    },
    doneIcon: Trash2,
    renderCancelled: ({ items, t }) => (
        <>{items.length > 1 ? t('docDeleteCancelledPlural') : t('docDeleteCancelled', { s: '' })}</>
    ),
};

// =========================================================================
// 2. Expense (transaction) deletion (expense_delete_request) — Preview +
// Download per row (only if document_id), X ALWAYS shown (#115 parity),
// removing last => cancel. testid: expense-delete-card
// =========================================================================
export const expenseDeleteConfig = {
    cardKey: 'expense_delete_request',
    testId: 'expense-delete-card',
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => Array.isArray(p?.expenses) && p.expenses.some((e) => e?.expense_id),
    getItems: (p) => (Array.isArray(p?.expenses) ? p.expenses.filter((e) => e?.expense_id) : []),
    itemKey: (e) => e.expense_id,
    cancelOnLastRemove: true,
    title: ({ t, count }) => t('expDeleteTitle', { n: count, s: plural(count) }),
    // #498 follow-up (PO): when a remaining row has a linked file, ask whether to
    // delete it too — unticked (KEEP) by default; sent to REST as delete_files.
    initialOptions: () => ({ deleteFiles: false }),
    renderOptions: ({ active, options, setOption, busy }) => (hasAttachedFile(active) ? (
        <AttachedFileChoice
            compact
            checked={options.deleteFiles}
            disabled={busy}
            onChange={(v) => setOption('deleteFiles', v)}
        />
    ) : null),
    renderRow: ({ item: e, ctx }) => {
        const { busy, removeFromQueue, onPreview, onDownload, t } = ctx;
        return (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-white" title={e.merchant}>{e.merchant || t('cardTransactionFallback', 'Transaction')}</div>
                    <div className="text-[11px] text-pai-meta">{fmtAmt(e.amount, e.currency)}{e.expense_date ? ` · ${e.expense_date}` : ''}</div>
                </div>
                {e.document_id && (
                    <button type="button" onClick={() => onPreview?.(e.document_id, e.merchant)} className={rowBtn('rose')} title={t('cardPreviewReceipt', 'Preview receipt')}><Eye size={12} /> {t('commonPreview', 'Preview')}</button>
                )}
                {e.document_id && (
                    <button type="button" onClick={() => onDownload?.(e.document_id, e.merchant)} className={rowBtn('rose')} title={t('cardDownloadReceipt', 'Download receipt')}><Download size={12} /> {t('commonDownload', 'Download')}</button>
                )}
                <RemoveX
                    show
                    onClick={() => removeFromQueue(e.expense_id)}
                    disabled={busy}
                    title={t('cardKeepItem', 'Keep {name} — remove from deletion', { name: e.merchant || t('cardThisTransaction', 'this transaction') })}
                    aria={`Remove ${e.merchant || 'this transaction'} from deletion`}
                />
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('expDeleteBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('expDeleteBusy'),
        run: async ({ items, options }) => runPerItem(items, async (e) => {
            await expenseService.deleteExpense(e.expense_id, {
                deleteFiles: !!(options?.deleteFiles && e.document_id),
            });
            // The detail is what lets an OPEN Finance panel drop the row and re-file
            // its totals at once instead of waiting for a (slow) refetch (#438).
            // `book_id` is omitted when the card payload predates it: undefined
            // means "unknown", whereas null would mean "Unassigned".
            emit('omnis:expense-deleted', {
                type: 'expense_deleted',
                id: e.expense_id,
                expense_id: e.expense_id,
                direction: e.direction,
                amount: e.amount,
                currency: e.currency,
                ...(e.book_id !== undefined ? { book_id: e.book_id } : {}),
            });
        }, { label: (e) => e.merchant || e.expense_id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('expDeleteDone', { n, s: plural(n) })}</>;
    },
    renderCancelled: ({ items, t }) => <>{t('expDeleteCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 3. Calendar event deletion (calendar_event_delete_request) — single detail,
// Confirm Deletion / Cancel. testid: calendar-delete-card
// =========================================================================
export const calendarDeleteConfig = {
    cardKey: 'calendar_event_delete_request',
    testId: 'calendar-delete-card',
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => !!p?.event_id,
    // Single-detail card — no items list.
    title: ({ t }) => t('calDeleteTitle'),
    renderDetail: ({ payload, t }) => {
        let whenLabel = '';
        if (payload.start_time) {
            const d = new Date(payload.start_time);
            if (!Number.isNaN(d.getTime())) whenLabel = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
        }
        return (
            <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2.5 text-sm text-white">
                <div className="font-medium truncate" title={payload.title}>{payload.title || 'Event'}</div>
                {(whenLabel || payload.location) && (
                    <div className="text-[11px] text-pai-meta">
                        {whenLabel}{payload.location ? `${whenLabel ? ' · ' : ''}${payload.location}` : ''}
                    </div>
                )}
                {payload.series_id && payload.count > 1 && (
                    <div data-testid="calendar-delete-series-count" data-count={payload.count} className="text-[11px] text-rose-300 mt-0.5">
                        {t('caleSeriesCount', { n: payload.count })}
                    </div>
                )}
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Check,
        label: ({ t }) => t('calDeleteConfirmBtn'),
        busyLabel: ({ t }) => t('calDeleteBusy'),
        run: async ({ payload }) => {
            try {
                // #532: a repeating event's card deletes its whole scope.
                await calendarService.deleteEvent(payload.event_id, payload.series_id ? payload.scope : undefined);
                emit('omnis:calendar-event-updated', { event_id: payload.event_id, reason: 'deleted' });
            } catch (err) {
                if (err?.status !== 404) return { count: 0, failuresText: 'Could not delete the event. Please try again.' };
            }
            return { count: payload.series_id ? (payload.count || 1) : 1 };
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t, payload }) => (payload?.series_id && payload.count > 1
        ? t('calDeleteSeriesRemoves', { n: payload.count })
        : t('calDeleteRemoves')),
    renderDone: ({ payload, t }) => <>{t('calDeleteDone', { title: payload.title })}</>,
    doneIcon: Trash2,
    renderCancelled: ({ payload, t }) => <>{t('calDeleteCancelled', { title: payload.title })}</>,
};

// =========================================================================
// 4. Batch calendar event deletion (calendar_events_delete_request) — title +
// date rows, X only when >1. testid: calendar-batch-delete-card
// =========================================================================
export const calendarEventsDeleteConfig = {
    cardKey: 'calendar_events_delete_request',
    testId: 'calendar-batch-delete-card',
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => Array.isArray(p?.events) && p.events.some((e) => e?.event_id),
    getItems: (p) => (Array.isArray(p?.events) ? p.events.filter((e) => e?.event_id) : []),
    itemKey: (e) => e.event_id,
    title: ({ t, count }) => t('calBatchTitle', { n: count, s: plural(count) }),
    renderRow: ({ item: e, ctx }) => {
        const { busy, active, removeFromQueue , t } = ctx;
        const whenLabel = (startTime) => {
            if (!startTime) return '';
            const d = new Date(startTime);
            return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
        };
        return (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-white" title={e.title}>{e.title || 'Event'}</div>
                    <div className="text-[11px] text-pai-meta">{whenLabel(e.start_time)}{e.location ? ` · ${e.location}` : ''}</div>
                </div>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(e.event_id)}
                    disabled={busy}
                    title={t('cardKeepItem', 'Keep {name} — remove from deletion', { name: e.title || t('cardThisEvent', 'this event') })}
                    aria={`Remove ${e.title || 'this event'} from deletion`}
                />
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('calBatchBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('calDeleteBusy'),
        run: async ({ items }) => runPerItem(items, async (e) => {
            await calendarService.deleteEvent(e.event_id);
            emit('omnis:calendar-event-updated', { event_id: e.event_id, reason: 'deleted' });
        }, { label: (e) => e.title || e.event_id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('calBatchDone', { n, s: plural(n) })}</>;
    },
    renderCancelled: ({ items, t }) => <>{t('calBatchCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 4b. Note deletion (note_delete_request) — Notes & Reminders (Fase 1). rose,
// title + content snippet rows, per-item X only when >1, dynamic
// "Delete N notes". testid: note-delete-card
// Payload: {notes:[{id,title,snippet}], count, action_id}.
// =========================================================================
export const noteDeleteConfig = {
    cardKey: 'note_delete_request',
    testId: 'note-delete-card',
    accent: 'rose',
    icon: StickyNote,
    isValid: (p) => Array.isArray(p?.notes) && p.notes.some((n) => n?.id),
    getItems: (p) => (Array.isArray(p?.notes) ? p.notes.filter((n) => n?.id) : []),
    itemKey: (n) => n.id,
    title: ({ t, count }) => t('noteDeleteTitle', { n: count, s: plural(count) }),
    renderRow: ({ item: n, ctx }) => {
        const { busy, active, removeFromQueue , t } = ctx;
        const label = n.title || (n.snippet ? '' : 'Untitled note');
        return (
            <div data-testid="note-delete-row" className="flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
                <StickyNote size={15} className="text-rose-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    {label && <div className="truncate text-sm font-medium text-white" title={label}>{label}</div>}
                    {n.snippet && <div className="truncate text-[11px] text-pai-meta" title={n.snippet}>{n.snippet}</div>}
                </div>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(n.id)}
                    disabled={busy}
                    title={t('cardKeepItem', 'Keep {name} — remove from deletion', { name: label || t('cardThisNote', 'this note') })}
                    aria={`Remove ${label || 'this note'} from deletion`}
                />
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('noteDeleteBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('noteDeleteBusy'),
        run: async ({ items }) => runPerItem(items, async (n) => {
            await noteService.remove(n.id);
            emit('omnis:note-deleted', { note_ids: [n.id] });
        }, { label: (n) => n.title || n.id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('noteDeleteDone', { n, s: plural(n) })}</>;
    },
    doneIcon: Trash2,
    renderCancelled: ({ items, t }) => <>{t('noteDeleteCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 4b'. Task deletion (task_delete_request) — Task List (#437). rose; one row
// per task (struck through when completed), per-item X only when >1, dynamic
// "Delete N tasks". The scope (selected | completed | all) only changes the
// title — "bórrame todo" reads as the whole list. Yes deletes EXACTLY the rows
// still listed, in ONE bulk call (an "everything" card can be dozens of rows).
// testid: task-delete-card
// Payload: {tasks:[{id,content,is_completed}], count, scope, action_id}.
// =========================================================================
export const taskDeleteConfig = {
    cardKey: 'task_delete_request',
    testId: 'task-delete-card',
    accent: 'rose',
    icon: ListChecks,
    isValid: (p) => Array.isArray(p?.tasks) && p.tasks.some((x) => x?.id),
    getItems: (p) => (Array.isArray(p?.tasks) ? p.tasks.filter((x) => x?.id) : []),
    itemKey: (x) => x.id,
    title: ({ t, count, payload }) => (payload?.scope === 'all'
        ? t('taskDeleteTitleAll', { n: count, s: plural(count) })
        : t('taskDeleteTitle', { n: count, s: plural(count) })),
    renderRow: ({ item: task, ctx }) => {
        const { busy, active, removeFromQueue, t } = ctx;
        return (
            <div data-testid="task-delete-row" className="flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
                <ListChecks size={15} className="text-rose-500 flex-shrink-0" />
                <div className={`flex-1 min-w-0 truncate text-sm ${task.is_completed ? 'line-through text-pai-meta' : 'text-white'}`} title={task.content}>
                    {task.content}
                </div>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(task.id)}
                    disabled={busy}
                    title={t('cardKeepItem', 'Keep {name} — remove from deletion', { name: task.content || t('cardThisTask', 'this task') })}
                    aria={`Remove ${task.content || 'this task'} from deletion`}
                />
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('taskDeleteBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('taskDeleteBusy'),
        run: async ({ items }) => {
            try {
                const res = await taskService.bulkDelete({ taskIds: items.map((x) => x.id) });
                emit('omnis:task-deleted', { task_ids: res?.task_ids || [] });
                return { count: items.length };
            } catch (err) {
                return { count: 0, failuresText: userErrorMessage(err) };
            }
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('taskDeleteDone', { n, s: plural(n) })}</>;
    },
    doneIcon: Trash2,
    renderCancelled: ({ items, t }) => <>{t('taskDeleteCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 4c. Reminder deletion (reminder_delete_request) — Notes & Reminders (Fase 1).
// delete_reminders CONVERTED from direct execution to this card flow. rose,
// title + local time rows, per-item X only when >1, dynamic "Delete N".
// testid: reminder-delete-card
// Payload: {reminders:[{id,title,remind_at_local}], count, action_id}.
// =========================================================================
// PAI Shared notes (#259) — the ticket's MANDATORY security confirmation.
// indigo, one row per recipient (each with an X so the user can trim the list
// before confirming), Share N / Cancel. testid: note-share-card
// Payload: {note_id, note_title, note_label, warning, team_name?,
// recipients:[{user_id,name,email}], count, action_id}.
//
// The warning text comes from the BACKEND (share_note.WARNING) rather than being
// re-authored here, so the sentence the PO specified is identical on the web
// card, in PAI's spoken reply, and in the Telegram prompt.
// =========================================================================
export const noteShareConfig = {
    cardKey: 'note_share_request',
    testId: 'note-share-card',
    accent: 'indigo',
    icon: Share2,
    isValid: (p) => !!p?.note_id && Array.isArray(p?.recipients) && p.recipients.some((r) => r?.user_id),
    getItems: (p) => (Array.isArray(p?.recipients) ? p.recipients.filter((r) => r?.user_id) : []),
    itemKey: (r) => r.user_id,
    title: ({ t, count, payload }) => t('noteShareCardTitle', {
        n: count, s: plural(count),
        note: payload?.note_label || payload?.note_title || 'this note',
    }),
    renderRow: ({ item: r, ctx }) => {
        const { busy, active, removeFromQueue , t } = ctx;
        const label = r.name || r.email || r.user_id;
        return (
            <div data-testid="note-share-recipient" className="flex items-center gap-2 rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] p-2">
                <UserPlus size={15} className="text-pai-blue flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-white" title={label}>{label}</div>
                    {r.email && r.email !== label && (
                        <div className="truncate text-[11px] text-pai-meta">{r.email}</div>
                    )}
                </div>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(r.user_id)}
                    disabled={busy}
                    title={t('cardDontShareWith', "Don't share with {name}", { name: label })}
                    aria={`Remove ${label} from this share`}
                />
            </div>
        );
    },
    action: {
        status: 'shared',
        icon: Check,
        label: ({ t, count }) => t('noteShareCardBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('noteShareCardBusy'),
        run: async ({ items, payload }) => runPerItem(items, async (r) => {
            await noteService.share(payload.note_id, { userId: r.user_id });
        }, { label: (r) => r.name || r.email || r.user_id, verb: 'share with' }),
    },
    cancel: { status: 'cancelled' },
    // The consequence sentence the ticket dictates, verbatim from the backend.
    footerNote: ({ payload, t }) => payload?.warning
        || t('noteShareCardWarning', 'This will give them full editing privileges to modify your note.'),
    doneIcon: Share2,
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('noteShareCardDone', { n, s: plural(n) })}</>;
    },
    renderCancelled: ({ t }) => <>{t('noteShareCardCancelled')}</>,
};

const fmtReminderWhen = (iso) => {
    if (!iso) return '';
    const d = new Date(iso); // remind_at_local is a tz-aware ISO string
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

export const reminderDeleteConfig = {
    cardKey: 'reminder_delete_request',
    testId: 'reminder-delete-card',
    accent: 'rose',
    icon: Bell,
    isValid: (p) => Array.isArray(p?.reminders) && p.reminders.some((r) => r?.id),
    getItems: (p) => (Array.isArray(p?.reminders) ? p.reminders.filter((r) => r?.id) : []),
    itemKey: (r) => r.id,
    title: ({ t, count }) => t('reminderDeleteTitle', { n: count, s: plural(count) }),
    renderRow: ({ item: r, ctx }) => {
        const { busy, active, removeFromQueue , t } = ctx;
        return (
            <div data-testid="reminder-delete-row" className="flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
                <Bell size={15} className="text-rose-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-white" title={r.title}>{r.title || 'Reminder'}</div>
                    {r.remind_at_local && <div className="text-[11px] text-pai-meta">{fmtReminderWhen(r.remind_at_local)}</div>}
                </div>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(r.id)}
                    disabled={busy}
                    title={t('cardKeepItem', 'Keep {name} — remove from deletion', { name: r.title || t('cardThisReminder', 'this reminder') })}
                    aria={`Remove ${r.title || 'this reminder'} from deletion`}
                />
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('reminderDeleteBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('reminderDeleteBusy'),
        run: async ({ items }) => runPerItem(items, async (r) => {
            await reminderService.deleteReminder(r.id);
            emit('omnis:reminder-deleted', { reminder_ids: [r.id] });
        }, { label: (r) => r.title || r.id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('reminderDeleteDone', { n, s: plural(n) })}</>;
    },
    doneIcon: Trash2,
    renderCancelled: ({ items, t }) => <>{t('reminderDeleteCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 4d. Ticket-comment deletion (comment_delete_request) — Comment section PAI
//     #242. delete_ticket_comments confirmation card. rose, author + snippet
// rows (+ ticket title), per-item X only when >1, dynamic "Delete N".
// testid: comment-delete-card
// Payload: {comments:[{id,author,snippet,ticket_id,ticket_title,board_id}],
// count, action_id}.
// =========================================================================
export const commentDeleteConfig = {
    cardKey: 'comment_delete_request',
    testId: 'comment-delete-card',
    accent: 'rose',
    icon: MessageSquare,
    isValid: (p) => Array.isArray(p?.comments) && p.comments.some((c) => c?.id),
    getItems: (p) => (Array.isArray(p?.comments) ? p.comments.filter((c) => c?.id) : []),
    itemKey: (c) => c.id,
    title: ({ t, count }) => t('commentDeleteTitle', { n: count, s: plural(count) }),
    renderRow: ({ item: c, ctx }) => {
        const { busy, active, removeFromQueue , t } = ctx;
        return (
            <div data-testid="comment-delete-row" className="flex items-center gap-2 rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
                <MessageSquare size={15} className="text-rose-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-white" title={c.author}>
                        {c.author || 'Someone'}
                        {c.ticket_title && <span className="text-[11px] font-normal text-pai-meta"> · {c.ticket_title}</span>}
                    </div>
                    {c.snippet && <div className="truncate text-[11px] text-pai-meta" title={c.snippet}>{c.snippet}</div>}
                </div>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(c.id)}
                    disabled={busy}
                    title={t('cardKeepComment', 'Keep this comment — remove from deletion')}
                    aria={`Remove ${c.author || 'this comment'} from deletion`}
                />
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('commentDeleteBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('commentDeleteBusy'),
        run: async ({ items }) => runPerItem(items, async (c) => {
            await boardService.deleteComment(c.id);
            // Refresh any open board so the ticket's comment count / thread updates.
            emit('omnis:ticket-updated', { board_id: c.board_id, ticket_id: c.ticket_id });
        }, { label: (c) => c.author || c.id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('commentDeleteDone', { n, s: plural(n) })}</>;
    },
    doneIcon: Trash2,
    renderCancelled: ({ items, t }) => <>{t('commentDeleteCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 5. Book deletion (book_delete_request) — numerical audit detail, Confirm
// Deletion / Cancel. testid: book-delete-card
// =========================================================================
export const bookDeleteConfig = {
    cardKey: 'book_delete_request',
    testId: 'book-delete-card',
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => !!p?.book_id,
    title: ({ t, payload }) => t('bookDeleteTitle', { name: payload.book_name }),
    renderDetail: ({ payload, t }) => {
        const tickets = Number(payload.ticket_count || 0);
        const lines = Number(payload.line_count || 0);
        const net = payload.net_balance;
        const currency = payload.currency || 'USD';
        return (
            <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2.5 text-sm text-white space-y-1">
                <div className="flex items-center justify-between"><span className="text-pai-meta">{t('bookDeleteTotalTickets')}</span><span className="font-semibold">{tickets}</span></div>
                <div className="flex items-center justify-between"><span className="text-pai-meta">{t('bookDeleteItemizedLines')}</span><span className="font-semibold">{lines}</span></div>
                <div className="flex items-center justify-between"><span className="text-pai-meta">{t('bookDeleteNetBalance')}</span><span className="font-semibold">{net != null ? `${net} ${currency}` : '—'}</span></div>
                {Number(payload.linked_file_count) > 0 && (
                    <div className="flex items-center justify-between"><span className="text-pai-meta">{t('bookDeleteLinkedFiles')}</span><span className="font-semibold">{Number(payload.linked_file_count)}</span></div>
                )}
            </div>
        );
    },
    // #498 follow-up 2 (PO): offer to batch-delete the book's linked files —
    // unticked (KEEP) by default; sent to REST as delete_files. Cards from before
    // this change carry no linked_file_count → no checkbox → files kept.
    initialOptions: () => ({ deleteFiles: false }),
    renderOptions: ({ payload, options, setOption, busy }) => (Number(payload.linked_file_count) > 0 ? (
        <LinkedFilesChoice
            compact
            count={Number(payload.linked_file_count)}
            checked={options.deleteFiles}
            disabled={busy}
            onChange={(v) => setOption('deleteFiles', v)}
        />
    ) : null),
    action: {
        status: 'deleted',
        icon: Check,
        label: ({ t }) => t('bookDeleteConfirmBtn'),
        busyLabel: ({ t }) => t('bookDeleteBusy'),
        run: async ({ payload, options }) => {
            try {
                await expenseService.deleteBook(payload.book_id, {
                    deleteTransactions: true,
                    deleteFiles: !!(options?.deleteFiles && Number(payload.linked_file_count) > 0),
                });
                emit('omnis:expense-updated', { reason: 'book_deleted', book_id: payload.book_id });
            } catch (err) {
                if (err?.status !== 404) return { count: 0, failuresText: 'Could not delete the book. Please try again.' };
                // Already gone — an open Finance panel must still drop it (#428).
                emit('omnis:expense-updated', { reason: 'book_deleted', book_id: payload.book_id });
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('bookDeleteNote'),
    renderDone: ({ payload, t }) => {
        const tickets = Number(payload.ticket_count || 0);
        const tx = tickets ? t('bookDeleteDoneTx', { n: tickets, s: plural(tickets) }) : '';
        return <>{t('bookDeleteDone', { name: payload.book_name, tx })}</>;
    },
    doneIcon: Trash2,
    renderCancelled: ({ payload, t }) => <>{t('bookDeleteCancelled', { name: payload.book_name })}</>,
};

// =========================================================================
// 5b. Book rename (book_rename_request) — Finance book name change (#464).
// Current → proposed name, Confirm / Cancel; Confirm runs the SAME PATCH the
// Finance panel's own rename uses (the BE refuses a name another of the user's
// books has, 409 → shown on the card). testid: book-rename-card
// =========================================================================
export const bookRenameConfig = {
    cardKey: 'book_rename_request',
    testId: 'book-rename-card',
    accent: 'indigo',
    icon: Pencil,
    isValid: (p) => !!p?.book_id && !!p?.new_name,
    title: ({ t }) => t('bookRenameTitle'),
    renderDetail: ({ payload, t }) => (
        <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-white/[0.03] p-2.5 text-sm text-white space-y-1.5">
            <div className="flex items-center justify-between gap-3">
                <span className="text-pai-meta">{t('bookRenameCurrent')}</span>
                <span data-testid="book-rename-current" className="font-semibold break-words text-right line-through decoration-white/40">{payload.current_name}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
                <span className="text-pai-meta">{t('bookRenameNew')}</span>
                <span data-testid="book-rename-new" className="font-semibold break-words text-right">{payload.new_name}</span>
            </div>
            <div className="text-[11px] text-pai-meta">{t('bookRenameBody')}</div>
        </div>
    ),
    action: {
        status: 'renamed',
        icon: Check,
        label: ({ t }) => t('bookRenameConfirmBtn'),
        busyLabel: ({ t }) => t('bookRenameBusy'),
        run: async ({ payload }) => {
            try {
                const book = await expenseService.updateBook(payload.book_id, { name: payload.new_name });
                emit('omnis:expense-updated', {
                    reason: 'book_renamed',
                    book_id: payload.book_id,
                    book_name: book?.name || payload.new_name,
                    old_name: payload.current_name,
                });
            } catch (err) {
                return { count: 0, failuresText: userErrorMessage(err) };
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    renderDone: ({ payload, t }) => <>{t('bookRenameDone', { old: payload.current_name, name: payload.new_name })}</>,
    doneIcon: Pencil,
    renderCancelled: ({ payload, t }) => <>{t('bookRenameCancelled', { old: payload.current_name })}</>,
};

// =========================================================================
// 5c. Rule change (rule_change_request) — Rulebook in Telegram (#503).
// edit_rulebook's add / update / delete card: the rule number and the current
// → proposed text, Confirm / Cancel. Confirm runs the REST rule route with the
// card's text as expected_text (409 `stale` when the Folio moved underneath),
// then fires `omnis:rulebook-updated` so an open Rulebook panel refreshes.
// testid: rule-change-card
// =========================================================================
const RULE_CHANGE_TITLE = { add: 'ruleChangeTitleAdd', update: 'ruleChangeTitleUpdate', delete: 'ruleChangeTitleDelete' };
const RULE_CHANGE_DONE = { add: 'ruleChangeDoneAdd', update: 'ruleChangeDoneUpdate', delete: 'ruleChangeDoneDelete' };

export const ruleChangeConfig = {
    cardKey: 'rule_change_request',
    testId: 'rule-change-card',
    accent: 'indigo',
    icon: ScrollText,
    isValid: (p) => !!RULE_CHANGE_TITLE[p?.action] && Number.isFinite(Number(p?.rule_number))
        && (p.action === 'delete' ? !!p.current_text : !!p.new_text),
    title: ({ t, payload }) => t(RULE_CHANGE_TITLE[payload.action], { n: payload.rule_number }),
    renderDetail: ({ payload, t }) => (
        <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-white/[0.03] p-2.5 text-sm text-white space-y-1.5">
            {payload.current_text && (
                <div>
                    <div className="text-pai-meta text-[11px]">{t('ruleChangeCurrent')}</div>
                    <div
                        data-testid="rule-change-current"
                        className={`whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${payload.action === 'update' || payload.action === 'delete' ? 'line-through decoration-white/40' : ''}`}
                    >
                        {payload.current_text}
                    </div>
                </div>
            )}
            {payload.new_text && (
                <div>
                    <div className="text-pai-meta text-[11px]">{t('ruleChangeNew')}</div>
                    <div data-testid="rule-change-new" className="font-semibold whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                        {payload.new_text}
                    </div>
                </div>
            )}
        </div>
    ),
    action: {
        status: 'applied',
        icon: Check,
        label: ({ t }) => t('ruleChangeConfirmBtn'),
        busyLabel: ({ t }) => t('ruleChangeBusy'),
        run: async ({ payload }) => {
            try {
                const result = await workspaceService.changeRule({
                    action: payload.action,
                    ruleNumber: payload.rule_number,
                    ruleText: payload.new_text,
                    expectedText: payload.current_text,
                });
                emit('omnis:rulebook-updated', result?.event || { reason: `rule_${payload.action}` });
            } catch (err) {
                return { count: 0, failuresText: userErrorMessage(err) };
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    renderDone: ({ payload, t }) => (
        <span data-testid="rule-change-done">{t(RULE_CHANGE_DONE[payload.action] || 'ruleChangeDoneUpdate', { n: payload.rule_number })}</span>
    ),
    doneIcon: ScrollText,
    renderCancelled: ({ t }) => <span data-testid="rule-change-cancelled">{t('ruleChangeCancelled')}</span>,
};

// =========================================================================
// 6. Batch ticket UPDATE (ticket_batch_update_request) — indigo, action_label
// header + button, X only when >1. testid: ticket-batch-update-card
// =========================================================================
export const ticketBatchUpdateConfig = {
    cardKey: 'ticket_batch_update_request',
    testId: 'ticket-batch-update-card',
    accent: 'indigo',
    icon: LayoutGrid,
    isValid: (p) => Array.isArray(p?.tickets) && p.tickets.some((t) => t?.ticket_id),
    getItems: (p) => (Array.isArray(p?.tickets) ? p.tickets.filter((t) => t?.ticket_id) : []),
    itemKey: (t) => t.ticket_id,
    title: ({ t, count, payload }) => t('ticketUpdateTitle', { action: payload?.action_label || 'Update', n: count, s: plural(count) }),
    renderRow: ({ item: tk, ctx }) => {
        const { busy, active, removeFromQueue } = ctx;
        return (
            <div className="flex items-center gap-2 rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] p-2">
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-white" title={tk.title}>{tk.title || 'Ticket'}</div>
                    {tk.current_column && <div className="text-[11px] text-pai-meta">{tk.current_column}</div>}
                </div>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(tk.ticket_id)}
                    disabled={busy}
                    title={`Keep ${tk.title || 'this ticket'} — remove from the batch`}
                    aria={`Remove ${tk.title || 'this ticket'} from the batch`}
                />
            </div>
        );
    },
    action: {
        status: 'updated',
        icon: Check,
        label: ({ t, count }) => t('ticketUpdateBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('ticketUpdateBusy'),
        run: async ({ items, payload }) => {
            const update = payload?.update || {};
            const boardId = payload?.board_id;
            return runPerItem(items, async (tk) => {
                await boardService.updateTicket(tk.ticket_id, update);
                emit('omnis:ticket-updated', { board_id: boardId, ticket_id: tk.ticket_id });
            }, { label: (tk) => tk.title || tk.ticket_id, tolerate404: false, verb: 'update' });
        },
    },
    cancel: { status: 'cancelled' },
    renderDone: ({ payload, count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('ticketUpdateDone', { action: payload?.action_label || 'Update', n, s: plural(n) })}</>;
    },
    doneIcon: LayoutGrid,
    renderCancelled: ({ items, t }) => <>{t('ticketUpdateCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 7. Batch ticket DELETION w/ preview (ticket_batch_delete_request) — rose,
// per-row Preview toggle expands description + labels, X when >1.
// testid: ticket-batch-delete-card
// =========================================================================
const TicketDeleteRow = ({ item: tk, ctx }) => {
    const { busy, active, removeFromQueue } = ctx;
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2">
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-white" title={tk.title}>{tk.title || 'Ticket'}</div>
                    {tk.column_name && <div className="text-[11px] text-pai-meta">{tk.column_name}</div>}
                </div>
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    disabled={busy}
                    data-testid="ticket-preview-toggle"
 className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-pai-blue bg-[rgba(74,144,240,0.1)] hover:bg-[rgba(74,144,240,0.16)] rounded disabled:opacity-40"
                >
                    <Eye size={11} /> {expanded ? ctx.t('cardHide') : ctx.t('cardPreview')}
                </button>
                <RemoveX
                    show={active.length > 1}
                    onClick={() => removeFromQueue(tk.ticket_id)}
                    disabled={busy}
                    title={`Keep ${tk.title || 'this ticket'} — remove from the batch`}
                    aria={`Remove ${tk.title || 'this ticket'} from the batch`}
                />
            </div>
            {expanded && (
                <div data-testid="ticket-preview-body" className="mt-2 pt-2 border-t border-pai-hairline space-y-1.5">
                    {Array.isArray(tk.labels) && tk.labels.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {tk.labels.map((l) => (
                                <span key={l.id || l.name} className="text-[10px] font-medium px-1.5 py-0.5 rounded text-white" style={{ background: l.color || '#6366f1' }}>
                                    {l.name}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="text-xs text-pai-secondary whitespace-pre-wrap">
                        {tk.description || <span className="italic text-pai-faint">{ctx.t('ticketNoDescription')}</span>}
                    </div>
                </div>
            )}
        </div>
    );
};

export const ticketBatchDeleteConfig = {
    cardKey: 'ticket_batch_delete_request',
    testId: 'ticket-batch-delete-card',
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => Array.isArray(p?.tickets) && p.tickets.some((t) => t?.ticket_id),
    getItems: (p) => (Array.isArray(p?.tickets) ? p.tickets.filter((t) => t?.ticket_id) : []),
    itemKey: (t) => t.ticket_id,
    title: ({ t, count }) => t('ticketDeleteTitle', { n: count, s: plural(count) }),
    renderRow: (props) => <TicketDeleteRow {...props} />,
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('ticketDeleteBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('ticketDeleteBusy'),
        run: async ({ items }) => runPerItem(items, async (tk) => {
            await boardService.deleteTicket(tk.ticket_id);
            emit('omnis:ticket-deleted', { ticket_id: tk.ticket_id });
        }, { label: (tk) => tk.title || tk.ticket_id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('ticketDeleteDone', { n, s: plural(n) })}</>;
    },
    renderCancelled: ({ items, t }) => <>{t('ticketDeleteCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 8. Batch BOARD deletion (board_batch_delete_request) — rose, per-row "Open
// Board" deep link, X when >1. testid: board-batch-delete-card
// =========================================================================
const BoardDeleteRow = ({ item: b, ctx }) => {
    const { busy, active, removeFromQueue, navigate , t } = ctx;
    return (
        <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2 flex items-center gap-2">
            <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium text-white" title={b.name}>{b.name || 'Board'}</div>
                <div className="text-[11px] text-pai-meta">
                    {b.ticket_count ?? 0} ticket{(b.ticket_count ?? 0) === 1 ? '' : 's'} · {b.member_count ?? 0} member{(b.member_count ?? 0) === 1 ? '' : 's'}
                </div>
            </div>
            <button
                type="button"
                onClick={() => navigate(`/boards?board=${b.board_id}`)}
                disabled={busy}
                data-testid="board-open-link"
 className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-pai-blue bg-[rgba(74,144,240,0.1)] hover:bg-[rgba(74,144,240,0.16)] rounded disabled:opacity-40"
            >
                <LayoutGrid size={11} /> {ctx.t('boardOpen')}
            </button>
            <RemoveX
                show={active.length > 1}
                onClick={() => removeFromQueue(b.board_id)}
                disabled={busy}
                title={t('cardKeepItemBatch', 'Keep {name} — remove from the batch', { name: b.name || t('cardThisBoard', 'this board') })}
                aria={`Remove ${b.name || 'this board'} from the batch`}
            />
        </div>
    );
};

export const boardBatchDeleteConfig = {
    cardKey: 'board_batch_delete_request',
    testId: 'board-batch-delete-card',
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => Array.isArray(p?.boards) && p.boards.some((b) => b?.board_id),
    getItems: (p) => (Array.isArray(p?.boards) ? p.boards.filter((b) => b?.board_id) : []),
    itemKey: (b) => b.board_id,
    title: ({ t, count }) => t('boardDeleteTitle', { n: count, s: plural(count) }),
    renderRow: (props) => <BoardDeleteRow {...props} />,
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t, count }) => t('boardDeleteBtn', { n: count, s: plural(count) }),
        busyLabel: ({ t }) => t('boardDeleteBusy'),
        run: async ({ items }) => runPerItem(items, async (b) => {
            await boardService.deleteBoard(b.board_id);
            emit('omnis:board-deleted', { board_id: b.board_id });
        }, { label: (b) => b.name || b.board_id, verb: 'delete' }),
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('boardDeleteDone', { n, s: plural(n) })}</>;
    },
    renderCancelled: ({ items, t }) => <>{t('boardDeleteCancelled', { s: items.length > 1 ? 's' : '' })}</>,
};

// =========================================================================
// 8b. Leave board (board_leave_request) — rose, single-detail card carrying the
// EXACT mandated script from the BE (#275: "I found the [Board Name]. Are
// you sure you want to exit? This action cannot be undone."), Confirm Exit /
// Cancel. Confirm runs the #274 self-leave route. testid: board-leave-card
// =========================================================================
export const boardLeaveConfig = {
    cardKey: 'board_leave_request',
    testId: 'board-leave-card',
    accent: 'rose',
    icon: LogOut,
    isValid: (p) => !!p?.board_id,
    title: ({ t, payload }) => t('boardLeaveTitle', { name: payload.board_name || 'board' }),
    renderDetail: ({ payload, t }) => (
        <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2.5 text-sm text-white space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
                <span
                    data-testid="board-leave-chip"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200"
                >
                    <LayoutGrid size={11} /> {payload.board_name || 'board'}
                </span>
            </div>
            {/* The BE-built script — deterministic, so the mandated wording always
                shows regardless of how the model phrases its reply. */}
            <div data-testid="board-leave-script" className="text-[12px] text-pai-secondary break-words">
                {payload.script || t('boardLeaveQuestion')}
            </div>
            <div className="text-[11px] text-pai-meta">
                {t('boardLeaveBody')}
            </div>
        </div>
    ),
    action: {
        status: 'left',
        icon: LogOut,
        label: ({ t }) => t('boardLeaveConfirmBtn'),
        busyLabel: ({ t }) => t('boardLeaveBusy'),
        run: async ({ payload }) => {
            try {
                await boardService.removeMember(payload.board_id, payload.user_id);
                emit('omnis:board-updated', { board_id: payload.board_id });
            } catch (err) {
                // 404 = membership already gone (e.g. left elsewhere): the user's
                // intent already holds, so treat it as done rather than erroring.
                if (err?.status !== 404) {
                    return { count: 0, failuresText: userErrorMessage(err) };
                }
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    renderDone: ({ payload, t }) => <>{t('boardLeaveDone', { name: payload.board_name || 'board' })}</>,
    doneIcon: LogOut,
    renderCancelled: ({ payload, t }) => <>{t('boardLeaveCancelled', { name: payload.board_name || 'board' })}</>,
};

// =========================================================================
// 9. Column move (column_move_request) — indigo, single detail, Confirm Move /
// Cancel. testid: column-move-card
// =========================================================================
export const columnMoveConfig = {
    cardKey: 'column_move_request',
    testId: 'column-move-card',
    accent: 'indigo',
    icon: LayoutGrid,
    isValid: (p) => !!p?.column_id && !!p?.target_board_id,
    title: ({ t }) => t('columnMoveTitle'),
    renderDetail: ({ payload, t }) => {
        const n = payload.ticket_count ?? 0;
        const bold = (v) => <span className="font-semibold">{v}</span>;
        return (
            <div className="text-sm text-pai-secondary mb-3">
                {tNodes(
                    t,
                    'columnMoveDetail',
                    'Moving the {column} column with {n} ticket{s} to {board} at {position}.',
                    {
                        column: bold(payload.column_name),
                        n: bold(n),
                        s: n === 1 ? '' : 's',
                        board: bold(payload.target_board_name),
                        position: payload.position_label || t('columnMoveEnd', 'the end'),
                    },
                )}
            </div>
        );
    },
    action: {
        status: 'confirmed',
        icon: LayoutGrid,
        label: ({ t }) => t('columnMoveConfirmBtn'),
        busyLabel: ({ t }) => t('columnMoveBusy'),
        run: async ({ payload }) => {
            try {
                await boardService.moveColumn(payload.column_id, payload.target_board_id, payload.position);
                emit('omnis:board-updated', { board_id: payload.target_board_id });
            } catch (err) {
                return { count: 0, failuresText: userErrorMessage(err) };
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    renderDone: ({ payload, t }) => <>{t('columnMoveDone', { name: payload.column_name, target: payload.target_board_name })}</>,
    doneIcon: LayoutGrid,
    renderCancelled: ({ t }) => <>{t('columnMoveCancelled')}</>,
};

// =========================================================================
// 9b. Label deletion (label_delete_request) — rose, single-detail card showing
// the label chip + the tickets that lose it, Confirm Deletion / Cancel.
// Deleting a label strips it from the whole palette AND every tagged ticket
//     (Label Management #212). testid: label-delete-card
// =========================================================================
export const labelDeleteConfig = {
    cardKey: 'label_delete_request',
    testId: 'label-delete-card',
    accent: 'rose',
    icon: Tag,
    isValid: (p) => !!p?.label_id,
    title: ({ t, payload }) => t('labelDeleteTitle', { name: payload.label_name }),
    renderDetail: ({ payload, t }) => {
        const tickets = Array.isArray(payload.tickets) ? payload.tickets : [];
        const n = Number(payload.ticket_count ?? tickets.length ?? 0);
        return (
            <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2.5 text-sm text-white space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        data-testid="label-delete-chip"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-white"
                        style={{ backgroundColor: payload.label_color || '#6366f1' }}
                    >
                        <Tag size={11} /> {payload.label_name || 'Label'}
                    </span>
                    {payload.board_name && (
                        <span className="text-[11px] text-pai-meta">{t('labelDeleteBoard', { board: payload.board_name })}</span>
                    )}
                </div>
                <div className="text-[12px] text-pai-secondary">
                    {n > 0 ? t('labelDeleteAffected', { n, s: plural(n) }) : t('labelDeleteNoTickets')}
                </div>
                {n > 0 && tickets.length > 0 && (
                    <ul data-testid="label-delete-tickets" className="list-disc pl-4 text-[11px] text-pai-meta space-y-0.5 max-h-24 overflow-y-auto">
                        {tickets.slice(0, 10).map((tk) => (
                            <li key={tk.ticket_id} className="truncate" title={tk.title}>{tk.title || 'Untitled'}</li>
                        ))}
                        {tickets.length > 10 && <li>{t('labelDeleteMore', { n: tickets.length - 10 })}</li>}
                    </ul>
                )}
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Check,
        label: ({ t }) => t('labelDeleteConfirmBtn'),
        busyLabel: ({ t }) => t('labelDeleteBusy'),
        run: async ({ payload }) => {
            try {
                await boardService.deleteLabel(payload.label_id);
                emit('omnis:board-updated', { board_id: payload.board_id });
            } catch (err) {
                if (err?.status !== 404) return { count: 0, failuresText: userErrorMessage(err) };
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('labelDeleteNote'),
    renderDone: ({ payload, t }) => <>{t('labelDeleteDone', { name: payload.label_name })}</>,
    doneIcon: Trash2,
    renderCancelled: ({ payload, t }) => <>{t('labelDeleteCancelled', { name: payload.label_name })}</>,
};

// =========================================================================
// 9c. Column deletion (column_delete_request) — rose, single-detail card showing
// the column + its board + the tickets that will be deleted with it, red
// Confirm Deletion / Cancel. Deleting a column removes it AND every ticket
// inside it (Column Management #204). testid: column-delete-card
// =========================================================================
export const columnDeleteConfig = {
    cardKey: 'column_delete_request',
    testId: 'column-delete-card',
    accent: 'rose',
    icon: LayoutGrid,
    isValid: (p) => !!p?.column_id,
    title: ({ t, payload }) => t('columnDeleteTitle', { name: payload.column_name }),
    renderDetail: ({ payload, t }) => {
        const tickets = Array.isArray(payload.tickets) ? payload.tickets : [];
        const n = Number(payload.ticket_count ?? tickets.length ?? 0);
        return (
            <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2.5 text-sm text-white space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        data-testid="column-delete-chip"
 className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/[0.09] text-white"
                    >
                        <LayoutGrid size={11} /> {payload.column_name || 'Column'}
                    </span>
                    {payload.board_name && (
                        <span className="text-[11px] text-pai-meta">{t('columnDeleteBoard', { board: payload.board_name })}</span>
                    )}
                </div>
                <div data-testid="column-delete-count" className="text-[12px] text-pai-secondary">
                    {n > 0 ? t('columnDeleteAffected', { n, s: plural(n) }) : t('columnDeleteEmpty')}
                </div>
                {n > 0 && tickets.length > 0 && (
                    <ul data-testid="column-delete-tickets" className="list-disc pl-4 text-[11px] text-pai-meta space-y-0.5 max-h-24 overflow-y-auto">
                        {tickets.slice(0, 10).map((tk) => (
                            <li key={tk.ticket_id} className="truncate" title={tk.title}>{tk.title || 'Untitled'}</li>
                        ))}
                        {tickets.length > 10 && <li>{t('columnDeleteMore', { n: tickets.length - 10 })}</li>}
                    </ul>
                )}
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Check,
        label: ({ t }) => t('columnDeleteConfirmBtn'),
        busyLabel: ({ t }) => t('columnDeleteBusy'),
        run: async ({ payload }) => {
            try {
                await boardService.deleteColumn(payload.column_id);
                emit('omnis:board-updated', { board_id: payload.board_id });
            } catch (err) {
                if (err?.status !== 404) return { count: 0, failuresText: userErrorMessage(err) };
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('columnDeleteNote'),
    renderDone: ({ payload, t }) => <>{t('columnDeleteDone', { name: payload.column_name })}</>,
    doneIcon: Trash2,
    renderCancelled: ({ payload, t }) => <>{t('columnDeleteCancelled', { name: payload.column_name })}</>,
};

// =========================================================================
// 10. Ticket document attach (ticket_attach_request) — indigo, Preview +
// Download per row, X ALWAYS shown, remove last => cancel.
// testid: ticket-attach-card
// =========================================================================
export const ticketAttachConfig = {
    cardKey: 'ticket_attach_request',
    testId: 'ticket-attach-card',
    accent: 'indigo',
    icon: Paperclip,
    isValid: (p) => Array.isArray(p?.documents) && p.documents.some((d) => d?.document_id) && !!p?.ticket_id,
    getItems: (p) => (Array.isArray(p?.documents) ? p.documents.filter((d) => d?.document_id) : []),
    itemKey: (d) => d.document_id,
    cancelOnLastRemove: true,
    title: ({ t, count }) => t('ticketAttachTitle', { n: count, s: plural(count) }),
    renderRow: ({ item: d, ctx }) => {
        const { busy, removeFromQueue, onPreview, onDownload, t } = ctx;
        return (
            <div className="flex items-center gap-2 rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] p-2">
                <FileText size={15} className="text-pai-faint flex-shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm text-white" title={d.filename}>{d.filename || t('cardDocumentFallbackCap', 'Document')}</span>
                <button type="button" onClick={() => onPreview?.(d.document_id, d.filename)} className={rowBtn('indigo')} title={t('commonPreview', 'Preview')}><Eye size={12} /> {t('commonPreview', 'Preview')}</button>
                <button type="button" onClick={() => onDownload?.(d.document_id, d.filename)} className={rowBtn('indigo')} title={t('commonDownload', 'Download')}><Download size={12} /> {t('commonDownload', 'Download')}</button>
                <RemoveX
                    show
                    onClick={() => removeFromQueue(d.document_id)}
                    disabled={busy}
                    title={t('cardRemoveFromSelection', 'Remove {name} from the selection', { name: d.filename || t('cardThisDocument', 'this document') })}
                    aria={`Remove ${d.filename || 'this document'} from the selection`}
                />
            </div>
        );
    },
    action: {
        status: 'uploaded',
        icon: Paperclip,
        label: ({ t }) => t('ticketAttachBtn'),
        busyLabel: ({ t }) => t('ticketAttachBusy'),
        run: async ({ items, payload }) => runPerItem(items, async (d) => {
            await boardService.addAttachment(payload.ticket_id, { documentId: d.document_id });
            emit('omnis:ticket-attachment-added', { ticket_id: payload.ticket_id });
        }, { label: (d) => d.filename || d.document_id, tolerate404: false, verb: 'attach' }),
    },
    cancel: { status: 'cancelled' },
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('ticketAttachDone', { n, s: plural(n) })}</>;
    },
    doneIcon: Paperclip,
    renderCancelled: ({ t }) => <>{t('ticketAttachCancelled')}</>,
};

// =========================================================================
// 11. Document rename (document_rename_request) — indigo, current → EDITABLE
// new-name input per row, X ALWAYS shown, remove last => cancel.
// testid: document-rename-card (Change name editable Box #131).
//
// The proposed new name is a live <input> the user can refine (spelling /
// casing / full rewrite). [Confirm Changes] reads the CURRENT input value of
// each remaining row at click time and renames to exactly that — zero extra
// chat turns. Uses a custom body (like the contact card) so the per-row
// edited values live in the card's own state and the action handler reads
// them directly.
// =========================================================================
const renameItems = (p) => (Array.isArray(p?.renames) ? p.renames.filter((r) => r?.document_id && r?.new_name) : []);

const RenameBody = ({ payload, ctx, t }) => {
    const items = useMemo(() => renameItems(payload), [payload]);
    // Per-row edited names, keyed by document_id, seeded from the proposed names.
    const [names, setNames] = useState(() => Object.fromEntries(items.map((r) => [r.document_id, r.new_name])));
    const [removed, setRemoved] = useState(() => new Set());
    const [localError, setLocalError] = useState(null);
    const { busy, setBusy, resolve, doCancel } = ctx;

    const remaining = items.filter((r) => !removed.has(r.document_id));

    const setName = (id, v) => setNames((m) => ({ ...m, [id]: v }));
    const drop = (id) => setRemoved((prev) => {
        const next = new Set(prev);
        next.add(id);
        // Removing the last row folds the whole card to cancelled (parity with the
        // old cancelOnLastRemove behaviour).
        if (items.filter((r) => !next.has(r.document_id)).length === 0) doCancel();
        return next;
    });

    const handleConfirm = async () => {
        // Capture the CURRENT input values at click time.
        const toRename = remaining.map((r) => ({ ...r, new_name: (names[r.document_id] || '').trim() }));
        if (toRename.some((r) => !r.new_name)) {
            setLocalError(t('docRenameEmptyName', 'A file name cannot be empty.'));
            return;
        }
        setBusy(true);
        setLocalError(null);
        const result = await runPerItem(toRename, async (r) => {
            await documentService.renameDocument(r.document_id, r.new_name);
            emit('omnis:document-renamed', { id: r.document_id, name: r.new_name });
        }, { label: (r) => r.current_name || r.document_id, tolerate404: false, verb: 'rename' });
        setBusy(false);
        if (result?.failuresText) { setLocalError(result.failuresText); return; }
        resolve('renamed', result.count);
    };

    return (
        <>
            <div className="space-y-1.5">
                {remaining.map((r) => (
                    <div key={r.document_id} data-testid="rename-row" className="flex items-center gap-2 rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] p-2 text-sm">
                        <span className="min-w-0 max-w-[35%] truncate text-pai-meta line-through" title={r.current_name}>{r.current_name}</span>
                        <ChevronRight size={14} className="flex-shrink-0 text-pai-blue" />
                        <input
                            type="text"
                            data-testid="rename-input"
                            value={names[r.document_id] ?? ''}
                            onChange={(e) => setName(r.document_id, e.target.value)}
                            disabled={busy}
                            title={t('docRenameInputTitle', 'Edit the new file name')}
                            aria-label={t('cardNewNameFor', 'New name for {name}', { name: r.current_name || t('cardFileNoun', 'file') })}
 className="flex-1 min-w-0 px-2 py-1 text-sm font-medium border border-[rgba(74,144,240,0.45)] rounded bg-white/[0.03] text-white focus:ring-1 focus:border-white/80"
                        />
                        <RemoveX
                            show
                            onClick={() => drop(r.document_id)}
                            disabled={busy}
                            title={t('cardKeepRename', 'Keep {name} — remove from the rename batch', { name: r.current_name || t('cardThisFile', 'this file') })}
                            aria={`Remove ${r.current_name || 'this file'} from the rename batch`}
                        />
                    </div>
                ))}
            </div>

            {localError && <div className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">{localError}</div>}

            <div className={CARD_ACTION_ROW}>
                <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={busy || remaining.length === 0}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-4 py-1 text-[11px] font-semibold rounded bg-white text-pai-void hover:bg-white/90 disabled:bg-white/30"
                >
                    <Check size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{busy ? t('docRenameBusy') : t('docRenameBtn')}</span>
                </button>
                <button
                    type="button"
                    onClick={doCancel}
                    disabled={busy}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-4 py-1 text-[11px] font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50"
                >
                    <X size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('cardCancel')}</span>
                </button>
            </div>
        </>
    );
};

export const documentRenameConfig = {
    cardKey: 'document_rename_request',
    testId: 'document-rename-card',
    accent: 'indigo',
    icon: Pencil,
    isValid: (p) => Array.isArray(p?.renames) && p.renames.some((r) => r?.document_id && r?.new_name),
    getItems: (p) => renameItems(p),
    itemKey: (r) => r.document_id,
    title: ({ t, count }) => t('docRenameTitle', { n: count, s: plural(count) }),
    customBody: ({ payload, ctx, t }) => <RenameBody payload={payload} ctx={ctx} t={t} />,
    action: { status: 'renamed' },   // done-state guard (custom body runs the rename)
    cancel: { status: 'cancelled' },
    renderDone: ({ count, active, t }) => {
        const n = count ?? active.length;
        return <>{t('docRenameDone', { n, s: plural(n) })}</>;
    },
    doneIcon: Pencil,
    renderCancelled: ({ t }) => <>{t('docRenameCancelled')}</>,
};

// =========================================================================
// 12. Contact creation (contact_creation_request) — indigo, editable form,
// Confirm / Decline / Edit. Confirmed => emerald summary.
// testid: contact-confirm-card
// =========================================================================
const ContactBody = ({ payload, ctx, t }) => {
    const initial = useMemo(() => ({
        name: payload?.name || '',
        email: payload?.email || '',
        phone: payload?.phone || '',
        company: payload?.company || '',
        position: payload?.position || '',
        industry: payload?.industry || '',
        omnis_username: payload?.omnis_username || '',
    }), [payload]);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(initial);
    const [localError, setLocalError] = useState(null);
    // Duplicate Guard (#260): the backend refuses a Name+Email / Name+Phone match
    // with a 409 carrying the existing entry, and the user picks what to do.
    const [duplicate, setDuplicate] = useState(null);
    const { busy, setBusy, resolve, compact, cardRef, adoptResolution } = ctx;

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleConfirm = async (override = {}) => {
        if (!form.name.trim()) { setLocalError(t('contactNameRequired')); return; }
        setBusy(true);
        setLocalError(null);
        try {
            const emails = [form.email.trim(), ...(payload?.emails || [])].map((s) => (s || '').trim()).filter(Boolean);
            const phones = [form.phone.trim(), ...(payload?.phones || [])].map((s) => (s || '').trim()).filter(Boolean);
            const created = await contactService.createContact({
                name: form.name.trim(),
                email: form.email.trim() || undefined,
                phone: form.phone.trim() || undefined,
                emails: emails.length ? emails : undefined,
                phones: phones.length ? phones : undefined,
                company: form.company.trim() || undefined,
                position: form.position.trim() || undefined,
                industry: form.industry.trim() || undefined,
                omnis_user_id: form.omnis_username.trim() || undefined,
                // #517: name the card — the server claims it in the same
                // transaction and refuses (creating nothing) when it was already
                // answered (declined here, or confirmed in another tab).
                card: (cardRef?.conversationId && cardRef?.messageId) ? {
                    conversation_id: cardRef.conversationId,
                    message_id: cardRef.messageId,
                    card_key: 'contact_creation_request',
                    entry_id: cardRef.entryId || undefined,
                    action_id: cardRef.actionId || undefined,
                } : undefined,
                ...override,
            });
            setDuplicate(null);
            emit('omnis:contact-changed', { type: 'contact_created', ...created });
            resolve('confirmed', undefined, { name: form.name.trim() });
        } catch (err) {
            if (err?.status === 409 && err?.body?.duplicate) {
                // Not an error state — a decision the user has to make.
                setDuplicate({ ...err.body.duplicate, prompt: err.message });
            } else if (err?.status === 409 && err?.body?.error?.type === 'card_already_resolved'
                && err?.body?.resolution?.status && adoptResolution) {
                // #517: this card was already answered — show that final state.
                setDuplicate(null);
                adoptResolution(err.body.resolution);
            } else {
                setLocalError(userErrorMessage(err));
            }
        } finally {
            setBusy(false);
        }
    };

    const handleDecline = () => { resolve('declined'); };

    const detail = (label, value) => value ? (
        <div><span className="text-pai-meta">{label}:</span> <span className="text-white">{value}</span></div>
    ) : null;

    return (
        <>
            {editing ? (
                <div className="grid grid-cols-2 gap-2">
                    {[
                        ['name', 'Name'], ['email', 'Email'], ['phone', 'Phone'],
                        ['company', 'Company'], ['position', 'Position'], ['industry', 'Industry'], ['omnis_username', 'PAI username'],
                    ].map(([key, label]) => (
                        <label key={key} className="block">
                            <span className="text-[10px] font-medium text-pai-meta">{label}</span>
                            <input
                                type="text"
                                value={form[key]}
                                onChange={(e) => update(key, e.target.value)}
 className="mt-0.5 w-full px-2 py-1 text-xs border border-pai rounded bg-white/[0.03] text-white"
                            />
                        </label>
                    ))}
                </div>
            ) : (
                <div className="rounded bg-white/[0.03] border border-[rgba(74,144,240,0.35)] p-2 text-[11px] space-y-0.5">
                    {detail('Name', form.name)}
                    {detail('Email', form.email)}
                    {detail('Phone', form.phone)}
                    {detail('Company', form.company)}
                    {detail('Position', form.position)}
                    {detail('Industry', form.industry)}
                    {detail('PAI username', form.omnis_username)}
                </div>
            )}

            {localError && <div className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">{localError}</div>}

            {/* Duplicate Guard (#260) — the backend found this person already in the
                Rolodex, so the card asks instead of silently creating a second entry. */}
            {duplicate && (
                <div
                    data-testid="contact-duplicate-warning"
                    className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2 text-[11px] text-amber-900 dark:text-amber-200"
                >
                    <div>{duplicate.prompt}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            data-testid="contact-duplicate-save-anyway"
                            onClick={() => handleConfirm({ allow_duplicate: true })}
                            disabled={busy}
 className="px-2 py-1 font-semibold rounded bg-white/[0.03] border border-amber-400 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
                        >
                            {t('contactDuplicateSaveAnyway')}
                        </button>
                        <button
                            type="button"
                            data-testid="contact-duplicate-update"
                            onClick={() => handleConfirm({ update_existing: true })}
                            disabled={busy}
 className="px-2 py-1 font-semibold rounded bg-white/[0.03] border border-amber-400 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
                        >
                            {t('contactDuplicateUpdate')}
                        </button>
                        <button
                            type="button"
                            data-testid="contact-duplicate-cancel"
                            onClick={() => { setDuplicate(null); handleDecline(); }}
                            disabled={busy}
 className="px-2 py-1 font-semibold rounded text-rose-600 dark:text-rose-400 bg-white/[0.03] border border-rose-300 dark:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50"
                        >
                            {t('contactDuplicateCancel')}
                        </button>
                    </div>
                </div>
            )}

            <div className={CARD_ACTION_ROW}>
                <button
                    type="button"
                    onClick={() => handleConfirm()}
                    disabled={busy || !!duplicate}
                    className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 rounded"
                >
                    <Check size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{busy ? t('contactConfirmBusy') : t('contactConfirmBtn')}</span>
                </button>
                <button
                    type="button"
                    onClick={handleDecline}
                    disabled={busy}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 bg-white/[0.03] border border-rose-300 dark:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded disabled:opacity-50"
                >
                    <X size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('contactDeclineBtn')}</span>
                </button>
                <button
                    type="button"
                    data-testid="contact-card-edit"
                    onClick={() => setEditing((v) => !v)}
                    disabled={busy}
                    aria-label={editing ? t('contactEditDoneBtn') : t('contactEditBtn')}
                    title={editing ? t('contactEditDoneBtn') : t('contactEditBtn')}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50"
                >
                    {/* #517: a narrow card (split view) shows the Edit icon only. */}
                    <Pencil size={12} className={CARD_ICON} />
                    {!compact && <span className={CARD_BTN_LABEL}>{editing ? t('contactEditDoneBtn') : t('contactEditBtn')}</span>}
                </button>
            </div>
        </>
    );
};

export const contactCreationConfig = {
    cardKey: 'contact_creation_request',
    testId: 'contact-confirm-card',
    summaryHasTestId: false,
    accent: 'indigo',
    icon: UserPlus,
    isValid: (p) => !!p?.name,
    title: ({ t, payload }) => t('contactConfirmTitle', { name: payload?.name || '' }),
    customBody: ({ payload, ctx, t, error }) => <ContactBody payload={payload} ctx={ctx} t={t} error={error} />,
    action: { status: 'confirmed' },       // used only for the done-state guard
    cancel: { status: 'declined' },
    doneClassName: 'mt-2 border border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg p-3 text-xs text-emerald-800 dark:text-emerald-300 flex items-start gap-2 min-w-0',
    doneIcon: Check,
    renderDone: ({ payload, doneMeta, t }) => {
        // "Contact <b>Name</b> created." — split the localized template on {name} so
        // the name renders bold, matching the original card.
        const tmpl = t('contactCreatedDone');
        const [pre, post = ''] = tmpl.split('{name}');
        const name = doneMeta?.name || payload?.resolution?.name || payload?.name;
        return <>{pre}<span className="font-semibold">{name}</span>{post}</>;
    },
    renderCancelled: ({ t }) => <>{t('contactDeclined')}</>,
};

// =========================================================================
// 12b. Event invite (event_invite_request) — Main Chat invite ticket. Create a
// calendar event AND invite network contacts + whole teams in ONE card.
// indigo, event details + recipient list (per-row X = edit who's invited),
// Confirm / Decline / Edit. testid: event-invite-card. On Confirm the event
// is saved and a Bell invitation goes to every remaining recipient.
// Payload: {event:{title,start_time,end_time,location,description},
// recipients:[{user_id,name,via}], invitee_user_ids:[...]}.
// #535 repeating series: + `series` {count, first_start, last_start, rule,
// summary} (the line `invite-series-summary`, localized from the structure)
// and either `recurrence` (a NEW series → posted back to with-invitations) or
// `existing` {event_id, scope} (an existing series → series-invitations).
// =========================================================================
const fmtWhen = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString(undefined, {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const EventInviteBody = ({ payload, ctx, t }) => {
    const { language } = useLanguage();
    const ev = payload?.event || {};
    const series = payload?.series && payload.series.count ? payload.series : null;
    const existing = payload?.existing || null;
    const recipients = useMemo(() => (Array.isArray(payload?.recipients) ? payload.recipients : []), [payload]);
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(ev.title || '');
    const [location, setLocation] = useState(ev.location || '');
    const [dropped, setDropped] = useState(() => new Set());
    const [localError, setLocalError] = useState(null);
    const [conflict, setConflict] = useState(null);   // #268 double-booking warning
    const { busy, setBusy, resolve, doCancel, compact } = ctx;

    const remaining = recipients.filter((r) => !dropped.has(r.user_id));
    const drop = (uid) => setDropped((prev) => { const n = new Set(prev); n.add(uid); return n; });

    // Confirming this card is a real booking, so it runs the same double-booking
    // guard as the manual Calendar form (#268): the BE answers 409
    // {conflict:true, conflicts:[…]} when the slot hard-overlaps an already
    // confirmed event. We surface the clash inline and only proceed if the user
    // picks "Book anyway", which replays the request with force:true.
    const submitInvite = async (force) => {
        setBusy(true);
        setLocalError(null);
        try {
            if (existing?.event_id) {
                // An existing event / series: nothing new is created (#535).
                const res = await calendarService.createSeriesInvitations(existing.event_id, {
                    invitee_user_ids: remaining.map((r) => r.user_id),
                    scope: existing.scope || 'following',
                });
                emit('omnis:calendar-event-updated', { event_id: existing.event_id });
                const n = res?.invited ?? remaining.length;
                resolve('confirmed', n, { invited: n, series_count: series ? (res?.count ?? series.count) : null, existing: true });
                return;
            }
            const res = await calendarService.createEventWithInvitations({
                title: title.trim() || ev.title,
                start_time: ev.start_time,
                end_time: ev.end_time,
                location: location.trim() || null,
                description: ev.description || null,
                invitee_user_ids: remaining.map((r) => r.user_id),
                recurrence: series ? (payload?.recurrence || null) : null,
                force,
            });
            emit('omnis:calendar-event-updated', { event_id: res?.event?.id });
            resolve('confirmed', res?.invited ?? remaining.length, {
                invited: res?.invited ?? remaining.length,
                series_count: series ? (res?.series?.count ?? series.count) : null,
            });
        } catch (err) {
            if (err?.status === 409 && err?.body?.conflict) {
                setConflict({ conflicts: err.body.conflicts || [] });
            } else {
                setLocalError(userErrorMessage(err));
            }
            setBusy(false);
        }
    };

    const handleConfirm = async () => {
        if (!remaining.length) { setLocalError(t('eventInviteNeedRecipient', 'Keep at least one recipient to invite.')); return; }
        setConflict(null);
        await submitInvite(false);
    };

    return (
        <>
            {/* Event details (Title / When / Location) — editable in Edit mode. */}
            <div className="rounded bg-white/[0.03] border border-[rgba(74,144,240,0.35)] p-2 text-[11px] space-y-1">
                {editing ? (
                    <div className="space-y-1.5">
                        <label className="block">
                            <span className="text-[10px] font-medium text-pai-meta">{t('commonTitle', 'Title')}</span>
                            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
 className="mt-0.5 w-full px-2 py-1 text-xs border border-pai rounded bg-white/[0.03] text-white" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-medium text-pai-meta">{t('commonLocation', 'Location')}</span>
                            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
 className="mt-0.5 w-full px-2 py-1 text-xs border border-pai rounded bg-white/[0.03] text-white" />
                        </label>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-1.5 text-white">
                            <Calendar size={12} className="text-pai-blue" />
                            <span className="font-semibold" data-testid="event-invite-title">{title}</span>
                        </div>
                        <div className="text-pai-meta">{fmtWhen(ev.start_time)}{location ? ` · ${location}` : ''}</div>
                        {series && (
                            <div
                                data-testid="invite-series-summary"
                                data-count={series.count}
                                className="flex items-start gap-1 text-pai-secondary"
                            >
                                <Repeat size={11} className="mt-0.5 shrink-0" />
                                <span className="break-words">{seriesSummary(series, t, language)}</span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Recipients — each row has an X to drop it from the batch (edit who). */}
            <div className="mt-2">
                <div className="text-[10px] uppercase tracking-wide text-pai-meta mb-1">
                    {t('eventInviteRecipients', 'Inviting')} ({remaining.length})
                </div>
                <ul className="rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] divide-y divide-white/[0.06] max-h-40 overflow-y-auto">
                    {remaining.map((r) => (
                        <li key={r.user_id} data-testid="event-invite-recipient" className="flex items-center gap-2 px-2.5 py-1.5 text-xs text-pai-secondary">
                            <span className="flex-1 min-w-0 truncate">{r.name}</span>
                            {r.via && r.via !== 'direct' && (
                                <span className="text-[10px] text-pai-blue text-pai-blue flex-shrink-0">via {r.via}</span>
                            )}
                            <RemoveX show={remaining.length > 1} onClick={() => drop(r.user_id)} disabled={busy}
                                title={t('eventInviteDropRecipient', 'Remove from invite')} aria={`Remove ${r.name}`} />
                        </li>
                    ))}
                </ul>
            </div>

            {localError && <div className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">{localError}</div>}

            {/* Double-booking warning (#268) — nothing was created; the user chooses. */}
            {conflict && (
                <div
                    data-testid="event-invite-conflict"
                    className="mt-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2 space-y-1.5"
                >
                    <div className="text-[11px] font-semibold text-amber-900 dark:text-amber-200">
                        {t('eventConflictTitle', 'This overlaps an event you have already confirmed:')}
                    </div>
                    <ul className="space-y-0.5">
                        {(conflict.conflicts || []).map((c) => (
                            <li key={c.id} data-testid="event-invite-conflict-item"
                                className="text-[11px] text-amber-900 dark:text-amber-200 break-words">
                                <span className="font-semibold">{c.title || 'Untitled event'}</span>
                                {(c.start_local || c.end_local) && (
                                    <span className="ml-1 opacity-80">
                                        ({c.start_local}{c.end_local ? ` – ${c.end_local}` : ''})
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                    <div className="flex items-center gap-2 pt-0.5">
                        <button type="button" data-testid="event-invite-conflict-confirm"
                            onClick={() => { setConflict(null); submitInvite(true); }} disabled={busy}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 rounded">
                            {t('eventConflictForce', 'Book anyway')}
                        </button>
                        <button type="button" data-testid="event-invite-conflict-cancel"
                            onClick={() => setConflict(null)} disabled={busy}
 className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50">
                            {t('eventConflictCancel', 'Cancel')}
                        </button>
                    </div>
                </div>
            )}

            <div className={CARD_ACTION_ROW}>
                <button type="button" onClick={handleConfirm} disabled={busy}
                    className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 rounded">
                    <Check size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{busy ? t('eventInviteConfirmBusy', 'Sending…') : t('eventInviteConfirmBtn', 'Confirm')}</span>
                </button>
                <button type="button" onClick={doCancel} disabled={busy}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 bg-white/[0.03] border border-rose-300 dark:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded disabled:opacity-50">
                    <X size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('eventInviteDeclineBtn', 'Decline')}</span>
                </button>
                {/* An EXISTING event/series (#535) is not re-titled from an invite card. */}
                {!existing && <button type="button" onClick={() => setEditing((v) => !v)} disabled={busy}
                    data-testid="event-invite-edit"
                    aria-label={editing ? t('eventInviteEditDone', 'Done') : t('eventInviteEditBtn', 'Edit')}
                    title={editing ? t('eventInviteEditDone', 'Done') : t('eventInviteEditBtn', 'Edit')}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50">
                    <Pencil size={12} className={CARD_ICON} />
                    {!compact && <span className={CARD_BTN_LABEL}>{editing ? t('eventInviteEditDone', 'Done') : t('eventInviteEditBtn', 'Edit')}</span>}
                </button>}
            </div>
        </>
    );
};

export const eventInviteConfig = {
    cardKey: 'event_invite_request',
    testId: 'event-invite-card',
    summaryHasTestId: false,
    accent: 'indigo',
    icon: Calendar,
    isValid: (p) => !!p?.event?.title && Array.isArray(p?.recipients) && p.recipients.length > 0,
    title: ({ t, payload }) => t('eventInviteCardTitle', 'Invite to “{title}”?', { title: payload?.event?.title || '' }),
    customBody: ({ payload, ctx, t }) => <EventInviteBody payload={payload} ctx={ctx} t={t} />,
    action: { status: 'confirmed' },   // done-state guard
    cancel: { status: 'declined' },
    doneClassName: 'mt-2 border border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg p-3 text-xs text-emerald-800 dark:text-emerald-300 flex items-start gap-2 min-w-0',
    doneIcon: Check,
    renderDone: ({ payload, count, doneMeta, t }) => {
        const n = doneMeta?.invited ?? payload?.resolution?.count ?? count ?? (payload?.recipients?.length || 0);
        const events = doneMeta?.series_count ?? payload?.series?.count;
        if (payload?.series?.count) {
            // #535: ONE invitation per person for the whole series.
            return <>{t(payload?.existing ? 'eventInviteSeriesExistingDone' : 'eventInviteSeriesDone',
                { count: n, events })}</>;
        }
        if (payload?.existing) return <>{t('eventInviteExistingDone', { count: n })}</>;
        return <>{t('eventInviteDone', 'Event created and {count} invitation(s) sent.', { count: n })}</>;
    },
    renderCancelled: ({ t }) => <>{t('eventInviteDeclined', 'Invitation cancelled.')}</>,
};

// =========================================================================
// 13. Team creation (team_create_request) — Agenda TO Contacts Phase C / T5.
// indigo, roster detail, Create Team / Cancel. testid: team-create-card
// Payload (from create_team tool, consumed DEFENSIVELY): {name, color?,
// description?, roster: [{user_id?, name?, email?}], member_user_ids?[]}.
// =========================================================================
const teamRoster = (p) => (Array.isArray(p?.roster) ? p.roster : []);
const teamMemberIds = (p) => (
    Array.isArray(p?.member_user_ids) && p.member_user_ids.length
        ? p.member_user_ids
        : teamRoster(p).map((r) => r?.user_id).filter(Boolean)
);

export const teamCreateConfig = {
    cardKey: 'team_create_request',
    testId: 'team-create-card',
    summaryHasTestId: false,
    accent: 'indigo',
    icon: Users,
    isValid: (p) => !!p?.name,
    title: ({ t, payload }) => t('teamCardCreateTitle', { name: payload?.name || '' }),
    renderDetail: ({ payload, t }) => {
        const roster = teamRoster(payload);
        return (
            <div className="space-y-2 mb-1">
                {/* Name only — no colour dot beside it ("Team Name"). The passive
                    swatch here fell back to a neutral grey for every team created
                    since #303 dropped the colour picker, so it showed a grey ball
                    next to the team name in the creation flow. */}
                <div className="flex items-center gap-2 text-sm text-pai-secondary">
                    <span className="font-semibold">{payload?.name}</span>
                </div>
                {roster.length === 0 ? (
                    <p className="text-xs text-pai-faint italic">{t('teamCardCreateNoMembers')}</p>
                ) : (
                    <ul className="rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] divide-y divide-white/[0.06]">
                        {roster.map((r, i) => (
                            <li key={r.user_id || r.email || i} data-testid="team-card-roster-row" className="px-2.5 py-1.5 text-xs text-pai-secondary truncate">
                                {r.name || r.email || r.user_id}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    },
    action: {
        status: 'confirmed',
        icon: Users,
        label: ({ t }) => t('teamCardCreateBtn'),
        busyLabel: ({ t }) => t('teamCardCreateBusy'),
        run: async ({ payload }) => {
            try {
                const res = await teamService.createTeam({
                    name: payload.name,
                    description: payload.description || undefined,
                    color: payload.color || undefined,
                    member_user_ids: teamMemberIds(payload),
                });
                emit('omnis:team-changed', { type: 'team_created', team: res?.team });
                return { count: 1 };
            } catch (err) {
                return { count: 0, failuresText: userErrorMessage(err) };
            }
        },
    },
    cancel: { status: 'cancelled' },
    doneClassName: 'mt-2 border border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg p-3 text-xs text-emerald-800 dark:text-emerald-300 flex items-start gap-2 min-w-0',
    doneIcon: Check,
    renderDone: ({ payload, t }) => <>{t('teamCardCreateDone', { name: payload?.name || '' })}</>,
    renderCancelled: ({ t }) => <>{t('teamCardCreateCancelled')}</>,
};

// =========================================================================
// 14. Team share (team_share_request) — share a document/book to a team OR
// create a team event. indigo, kind-aware detail, Confirm / Cancel.
// testid: team-share-card. Payload (DEFENSIVE): {team_id, team_name,
// kind:'document'|'book'|'event', asset:{id,name,...} | asset_id,
// role_overrides?}.
// =========================================================================
const shareAssetId = (p) => p?.asset?.id || p?.asset_id || null;
const shareAssetName = (p) => p?.asset?.name || p?.asset?.filename || p?.asset?.title || p?.asset_name || '';
const SHARE_ICONS = { document: FileText, book: DollarSign, event: Calendar };

export const teamShareConfig = {
    cardKey: 'team_share_request',
    testId: 'team-share-card',
    accent: 'indigo',
    icon: Share2,
    isValid: (p) => !!p?.team_id && !!p?.kind,
    title: ({ t, payload }) => t('teamCardShareTitle', { team: payload?.team_name || '' }),
    renderDetail: ({ payload, t }) => {
        const kind = payload?.kind;
        const KindIcon = SHARE_ICONS[kind] || FileText;
        const label = kind === 'event'
            ? (payload?.asset?.title || payload?.title || t('teamCardShareEventFallback'))
            : shareAssetName(payload) || t('teamCardShareAssetFallback');
        const line = kind === 'document' ? t('teamCardShareDocBody', { team: payload?.team_name || '' })
            : kind === 'book' ? t('teamCardShareBookBody', { team: payload?.team_name || '' })
                : t('teamCardShareEventBody', { team: payload?.team_name || '' });
        return (
            <div className="space-y-2 mb-1">
                <p className="text-sm text-pai-secondary">{line}</p>
                <div className="flex items-center gap-2 rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] p-2">
                    <KindIcon size={15} className="text-pai-faint flex-shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-sm text-white" title={label}>{label}</span>
                </div>
            </div>
        );
    },
    action: {
        status: 'confirmed',
        icon: Check,
        label: ({ t }) => t('teamCardShareBtn'),
        busyLabel: ({ t }) => t('teamCardShareBusy'),
        run: async ({ payload }) => {
            try {
                if (payload.kind === 'event') {
                    const ev = payload.asset || payload.event || {};
                    await teamService.createTeamEvent(payload.team_id, {
                        title: ev.title || payload.title,
                        start_time: ev.start_time || payload.start_time,
                        end_time: ev.end_time || payload.end_time,
                        location: ev.location || payload.location,
                        description: ev.description || payload.description,
                    });
                    emit('omnis:calendar-event-updated', { team_id: payload.team_id });
                } else {
                    await teamService.shareAsset(payload.team_id, {
                        kind: payload.kind,
                        assetId: shareAssetId(payload),
                        roleOverrides: payload.role_overrides || null,
                    });
                }
                emit('omnis:team-changed', { type: 'team_share', team_id: payload.team_id });
                return { count: 1 };
            } catch (err) {
                return { count: 0, failuresText: userErrorMessage(err) };
            }
        },
    },
    cancel: { status: 'cancelled' },
    doneIcon: Check,
    renderDone: ({ payload, t }) => <>{t('teamCardShareDone', { team: payload?.team_name || '' })}</>,
    renderCancelled: ({ t }) => <>{t('teamCardShareCancelled')}</>,
};

// =========================================================================
// 15. Team deletion (team_delete_request) — creator-only Smithereens. rose,
// numerical audit + warning, Delete Team / Cancel. testid: team-delete-card
// Payload (DEFENSIVE): {team_id, team_name, member_count?, share_count?}.
// =========================================================================
export const teamDeleteConfig = {
    cardKey: 'team_delete_request',
    testId: 'team-delete-card',
    accent: 'rose',
    icon: Trash2,
    isValid: (p) => !!p?.team_id,
    title: ({ t, payload }) => t('teamCardDeleteTitle', { name: payload?.team_name || '' }),
    renderDetail: ({ payload, t }) => {
        const members = payload?.member_count ?? payload?.summary?.members ?? 0;
        const shares = payload?.share_count ?? payload?.summary?.shares ?? 0;
        return (
            <div className="space-y-2 mb-1">
                <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2 text-center">
                        <div className="text-lg font-semibold text-white tabular-nums">{members}</div>
                        <div className="text-[10px] uppercase tracking-wide text-pai-meta">{t('teamCardDeleteMembers')}</div>
                    </div>
                    <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2 text-center">
                        <div className="text-lg font-semibold text-white tabular-nums">{shares}</div>
                        <div className="text-[10px] uppercase tracking-wide text-pai-meta">{t('teamCardDeleteShares')}</div>
                    </div>
                </div>
                <p className="text-xs text-rose-600 dark:text-rose-400">{t('teamDeleteDisclaimer')}</p>
            </div>
        );
    },
    action: {
        status: 'deleted',
        icon: Trash2,
        label: ({ t }) => t('teamCardDeleteBtn'),
        busyLabel: ({ t }) => t('teamCardDeleteBusy'),
        run: async ({ payload }) => {
            try {
                await teamService.deleteTeam(payload.team_id);
                emit('omnis:team-changed', { type: 'team_deleted', team_id: payload.team_id });
                return { count: 1 };
            } catch (err) {
                return { count: 0, failuresText: userErrorMessage(err) };
            }
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('cardCannotUndo'),
    doneIcon: Trash2,
    renderDone: ({ payload, t }) => <>{t('teamCardDeleteDone', { name: payload?.team_name || '' })}</>,
    renderCancelled: ({ t }) => <>{t('teamCardDeleteCancelled')}</>,
};

// =========================================================================
// 16. Direct send (direct_send_request) — PAI-to-PAI delivery of a document or
// a contact card to a LINKED contact (Agenda TO Contacts Phase D / T6 + T4).
// indigo, single-detail "<asset> → <contact>", Confirm Send / Cancel.
// testid: direct-send-card. Payload (from send_document_to_contact /
// send_contact_card): {contact_id, contact_name, kind:'document'|
// 'contact_card', asset:{id,name}, action_id}.
//
// Custom body (like the contact card) so the send result's phone-delivery status
//     ('sent' vs Bell-only) can surface in the done summary via resolve()'s meta.
//     #530: the BE still reports it under its legacy channel key; the card only
//     ever says "Delivered to their PAI" / Bell-only and never names the channel.
// =========================================================================
const DirectSendBody = ({ payload, ctx, t }) => {
    const { busy, setBusy, resolve, doCancel } = ctx;
    const [localError, setLocalError] = useState(null);
    const kind = payload?.kind;
    const KindIcon = kind === 'contact_card' ? ContactIcon : FileText;
    const kindLabel = kind === 'contact_card' ? t('directSendCardLabel') : t('directSendDocLabel');
    const assetName = payload?.asset?.name || payload?.asset?.filename || '';

    const handleConfirm = async () => {
        setBusy(true);
        setLocalError(null);
        try {
            const res = await apiService.post(`/api/contacts/${payload.contact_id}/send`, {
                kind,
                asset_id: payload?.asset?.id,
            });
            resolve('confirmed', undefined, { phoneDelivery: res?.whatsapp });
        } catch (err) {
            // The send route returns the FLAT {error:'<code>', message:'<friendly>'}
            // shape (contact_network blueprint) — apiError keeps the code
            // discriminator in err.message and stashes the raw body on err.body, so
            // prefer the friendly BE message (e.g. the 409 already_sent
            // "You already sent <name> to this contact.") over the bare code.
            setLocalError(userErrorMessage(err));
            setBusy(false);
        }
    };

    return (
        <>
            <div className="flex items-center gap-2 rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] p-2">
                <KindIcon size={15} className="text-pai-faint flex-shrink-0" />
                <span className="min-w-0 flex-1 truncate text-sm text-white" title={assetName}>
                    <span className="text-[10px] uppercase tracking-wide text-pai-faint mr-1.5">{kindLabel}</span>
                    {assetName}
                </span>
                <ChevronRight size={14} className="flex-shrink-0 text-pai-blue" />
                <span className="min-w-0 max-w-[40%] truncate text-sm font-medium text-white" title={payload?.contact_name}>
                    {payload?.contact_name}
                </span>
            </div>

            {localError && (
                <div data-testid="direct-send-error" className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">
                    {localError}
                </div>
            )}

            <div className={CARD_ACTION_ROW}>
                <button
                    type="button"
                    data-testid="direct-send-confirm"
                    onClick={handleConfirm}
                    disabled={busy}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-4 py-1 text-[11px] font-semibold rounded bg-white text-pai-void hover:bg-white/90 disabled:bg-white/30"
                >
                    <Send size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{busy ? t('directSendBusy') : t('directSendConfirmBtn')}</span>
                </button>
                <button
                    type="button"
                    onClick={doCancel}
                    disabled={busy}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-4 py-1 text-[11px] font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50"
                >
                    <X size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('cardCancel')}</span>
                </button>
            </div>
        </>
    );
};

export const directSendConfig = {
    cardKey: 'direct_send_request',
    testId: 'direct-send-card',
    accent: 'indigo',
    icon: Send,
    isValid: (p) => !!p?.contact_id && !!p?.asset?.id && !!p?.kind,
    title: ({ t, payload }) => t('directSendTitle', { contact: payload?.contact_name || '' }),
    customBody: ({ payload, ctx, t }) => <DirectSendBody payload={payload} ctx={ctx} t={t} />,
    action: { status: 'confirmed' },   // used only for the done-state guard
    cancel: { status: 'cancelled' },
    doneClassName: 'mt-2 border border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-lg p-3 text-xs text-emerald-800 dark:text-emerald-300 flex items-start gap-2 min-w-0',
    doneIcon: Check,
    renderDone: ({ payload, doneMeta, t }) => {
        const assetName = payload?.asset?.name || payload?.asset?.filename || '';
        // doneMeta.phoneDelivery is session-only (undefined after reload) — fall
        // back to the generic "delivered to their PAI" line.
        const phoneDelivery = doneMeta?.phoneDelivery;
        const statusLine = phoneDelivery && phoneDelivery !== 'sent'
            ? t('directSendBellOnly')
            : t('directSendSent');
        return (
            <span data-testid="direct-send-status">
                {t('directSendDone', { asset: assetName, contact: payload?.contact_name || '' })}
                {' '}
                <span className="text-emerald-600 dark:text-emerald-400">{statusLine}</span>
            </span>
        );
    },
    renderCancelled: ({ t }) => <>{t('directSendCancelled')}</>,
};

// =========================================================================
// 22. Action revocation / UNDO (action_revoke_request) — Double tap W/T #210.
// rose, single-detail card naming the SPECIFIC entity an earlier action
// created ("USD 50.00 at Starbucks"), with Confirm / Cancel. Confirming
// calls POST /api/actions/<id>/revoke, which removes what the action made
//     (a created DOCUMENT is moved to Recently Deleted, never purged).
// Payload: {target_action_id, category, label, summary, tool, action_id}.
// NOTE `target_action_id` vs `action_id`: the latter is the ledger's own id
// for THIS pending card (used to resolve it); the former is the action being
// undone. They are different rows and must never be conflated.
// testid: action-revoke-card
// =========================================================================

// Which open panel should refresh once an action is undone. Best-effort: an
// unknown category simply skips the broadcast (the card still resolves).
const REVOKE_REFRESH_EVENTS = {
    expense: 'omnis:expense-deleted',
    calendar: 'omnis:calendar-event-updated',
    reminder: 'omnis:reminder-deleted',
    note: 'omnis:note-deleted',
    ticket: 'omnis:ticket-deleted',
    board: 'omnis:board-deleted',
    contact: 'omnis:contact-changed',
    document: 'omnis:document-deleted',
};

export const actionRevokeConfig = {
    cardKey: 'action_revoke_request',
    testId: 'action-revoke-card',
    accent: 'rose',
    icon: Undo2,
    isValid: (p) => !!p?.target_action_id,
    title: ({ t, payload }) => t('actionRevokeTitle', { label: payload.label || 'action' }),
    renderDetail: ({ payload, t }) => (
        <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-white/[0.03] p-2.5 text-sm text-white space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
                <span
                    data-testid="action-revoke-chip"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200"
                >
                    <Undo2 size={11} /> {payload.label || 'action'}
                </span>
            </div>
            {payload.summary && (
                <div
                    data-testid="action-revoke-summary"
 className="text-[12px] text-pai-secondary break-words"
                >
                    {payload.summary}
                </div>
            )}
            <div className="text-[11px] text-pai-meta">
                {payload.category === 'document'
                    ? t('actionRevokeDocNote')
                    : t('actionRevokeBody', { label: payload.label || 'action' })}
            </div>
        </div>
    ),
    action: {
        status: 'revoked',
        icon: Check,
        label: ({ t }) => t('actionRevokeConfirmBtn'),
        busyLabel: ({ t }) => t('actionRevokeBusy'),
        run: async ({ payload }) => {
            try {
                const res = await actionService.revoke(payload.target_action_id);
                const evt = REVOKE_REFRESH_EVENTS[res?.category || payload.category];
                if (evt) emit(evt, { ids: res?.entity_ids || [], via: 'revoke_action' });
            } catch (err) {
                // 409 = already reversed: the user's intent already holds, so treat
                // it as done rather than stranding the card in an error state.
                if (err?.status !== 409 && err?.status !== 404) {
                    return { count: 0, failuresText: userErrorMessage(err) };
                }
            }
            return { count: 1 };
        },
    },
    cancel: { status: 'cancelled' },
    footerNote: ({ t }) => t('actionRevokeNote'),
    renderDone: ({ payload, t }) => <>{t('actionRevokeDone', { label: payload.label || 'action' })}</>,
    doneIcon: Undo2,
    renderCancelled: ({ payload, t }) => <>{t('actionRevokeCancelled', { label: payload.label || 'action' })}</>,
};

// =========================================================================
// 20. Add Rule (rule_add_request) — PAI Rulebook #146, sub-feature 4.
//
// The Assistant Desk's scribe proposes a finished rule; this card is the
// ONLY thing that files it into the Folio. THREE buttons, so it needs a
// customBody: the engine's default chrome has exactly two slots (action +
// cancel), and [Edit] is a real third outcome.
//
//     [Add Rule] appends server-side (the BE owns the numbering) and fires
//     `omnis:rulebook-updated` so the Folio shows it immediately.
//     [Edit] is NON-destructive and writes nothing: the draft stays on the
// Desk for refinement. It persists as its own `editing` status
// so a reload doesn't re-arm a card the user already stepped
// away from — but because that status is neither the action nor
// the cancel one, the engine would fall straight back through to
// the pending render, so this body owns the editing state itself
//                (seeded from payload.resolution) and persists it directly.
//     [Cancel] dismisses the draft.
// =========================================================================

const RuleAddBody = ({ payload, ctx, t }) => {
    // The engine does not forward its own conversationId/messageId into a
    // customBody, so Message.jsx threads them through `cardProps`, which the
    // engine spreads into ctx.
    const { busy, setBusy, resolve, conversationId, messageId } = ctx;
    const [localError, setLocalError] = useState(null);
    // `editing` is owned here, not by the engine — see the block comment above.
    const [editing, setEditing] = useState(payload?.resolution?.status === 'editing');

    const ruleText = (payload?.rule_text || '').trim();

    const handleAdd = async () => {
        setBusy(true);
        setLocalError(null);
        try {
            const result = await workspaceService.appendRule(ruleText);
            // The Folio listens for this and shows the new rule live. The rule
            // text rides along so a Folio with unsaved local edits can merge it
            // in rather than refetch over what the user is typing.
            emit('omnis:rulebook-updated', {
                rule_text: result.rule_text,
                rule_number: result.rule_number,
                content: result.content,
            });
            resolve('added', undefined, {
                ruleNumber: result.rule_number,
                ruleText: result.rule_text,
            });
        } catch (err) {
            setLocalError(userErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const handleEdit = async () => {
        setEditing(true);
        // Persisted directly (not through `resolve`) so the engine stays on the
        // pending branch and keeps rendering this body.
        await persistResolution({
            conversationId,
            messageId,
            cardKey: 'rule_add_request',
            status: 'editing',
            actionId: payload?.action_id || null,
        });
    };

    return (
        <>
            <div
                data-testid="rule-add-text"
 className="rounded-lg border border-[rgba(74,144,240,0.35)] bg-white/[0.03] p-2.5 text-sm text-white whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
            >
                {ruleText}
            </div>

            {localError && (
                <div data-testid="rule-add-error" className="mt-2 text-[11px] text-rose-600 dark:text-rose-400">
                    {localError}
                </div>
            )}

            {editing ? (
                <div
                    data-testid="rule-add-editing"
 className="mt-3 flex items-center gap-2 text-[11px] text-pai-secondary"
                >
                    <Pencil size={12} className="flex-shrink-0" />
                    {t('ruleAddEditingHint')}
                </div>
            ) : (
                <div className={CARD_ACTION_ROW}>
                    <button
                        type="button"
                        data-testid="rule-add-confirm"
                        onClick={handleAdd}
                        disabled={busy || !ruleText}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold bg-white text-pai-void hover:bg-white/90 disabled:bg-white/30 rounded"
                    >
                        <Check size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{busy ? t('ruleAddBusy') : t('ruleAddConfirmBtn')}</span>
                    </button>
                    <button
                        type="button"
                        data-testid="rule-add-edit"
                        onClick={handleEdit}
                        disabled={busy}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-pai-secondary bg-white/[0.03] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50"
                    >
                        <Pencil size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('ruleAddEditBtn')}</span>
                    </button>
                    <button
                        type="button"
                        data-testid="rule-add-cancel"
                        onClick={() => resolve('cancelled')}
                        disabled={busy}
 className="inline-flex items-center gap-1 max-w-full min-w-0 px-3 py-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 bg-white/[0.03] border border-rose-300 dark:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded disabled:opacity-50"
                    >
                        <X size={12} className={CARD_ICON} /> <span className={CARD_BTN_LABEL}>{t('cardCancel')}</span>
                    </button>
                </div>
            )}
        </>
    );
};

export const ruleAddConfig = {
    cardKey: 'rule_add_request',
    testId: 'rule-add-card',
    summaryHasTestId: false,
    accent: 'indigo',
    icon: ScrollText,
    isValid: (p) => !!(p?.rule_text || '').trim(),
    title: ({ t }) => t('ruleAddTitle'),
    customBody: ({ payload, ctx, t }) => <RuleAddBody payload={payload} ctx={ctx} t={t} />,
    action: { status: 'added' },   // done-state sentinel only (customBody owns the buttons)
    cancel: { status: 'cancelled' },
 doneClassName: 'mt-2 border border-[rgba(74,144,240,0.45)] bg-[rgba(74,144,240,0.07)] rounded-lg p-3 text-xs text-pai-blue text-pai-blue flex items-start gap-2 min-w-0',
    doneIcon: ScrollText,
    renderDone: ({ payload, doneMeta, t }) => (
        <span data-testid="rule-add-done">
            {t('ruleAddDone', {
                rule: doneMeta?.ruleText || payload?.resolution?.rule_text || payload?.rule_text || '',
            })}
        </span>
    ),
    renderCancelled: ({ t }) => <span data-testid="rule-add-cancelled">{t('ruleAddCancelled')}</span>,
};
